/**
 * WebsiteFilters
 *
 * Filter bar and sorting controls for website templates
 * Supports Local and State tags, saved filter, and sorting options
 */
import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronDown, Clock, Shuffle, X } from 'lucide-react';
import { WEBSITE_TAG_LIST } from '../../../data/tags/tagEnums';

function WebsiteFilters({
  activeFilter,
  setActiveFilter,
  sortMethod,
  setSortMethod,
  searchLoading,
  isLoggedIn = true,
  isPremiumUser = true,
  showSavedWebsites = false,
  setShowSavedWebsites
}) {
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const sortMenuRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const [sortDropdownPosition, setSortDropdownPosition] = useState({ top: 0, left: 0 });

  // Update dropdown positions on scroll and window resize
  useEffect(() => {
    const updateDropdownPositions = () => {
      if (isSortMenuOpen && sortMenuRef.current) {
        const rect = sortMenuRef.current.getBoundingClientRect();
        setSortDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: Math.min(rect.left + window.scrollX, window.innerWidth - 160)
        });
      }
    };
    
    if (isSortMenuOpen) {
      window.addEventListener('scroll', updateDropdownPositions);
      window.addEventListener('resize', updateDropdownPositions);
      updateDropdownPositions();
    }
    
    return () => {
      window.removeEventListener('scroll', updateDropdownPositions);
      window.removeEventListener('resize', updateDropdownPositions);
    };
  }, [isSortMenuOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target) && 
          sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setIsSortMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Tag toggle handler
  const handleTagToggle = (tag) => {
    if (activeFilter === tag) {
      // Remove the tag
      setActiveFilter(null);
    } else {
      // Set the tag
      setActiveFilter(tag);
    }
  };

  // Clear filter
  const clearFilter = () => {
    setActiveFilter(null);
  };

  // Sort options
  const sortOptions = [
    { value: 'random', label: 'Randomize', icon: Shuffle },
    { value: 'newest', label: 'Newly added', icon: Clock }
  ];

  const currentSortOption = sortOptions.find(option => option.value === sortMethod) || sortOptions[0];

  return (
    <div className="mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Tags Section */}
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
          {/* Visible Tags */}
          {WEBSITE_TAG_LIST.map((tag) => (
            <button
              key={tag}
              onClick={() => handleTagToggle(tag)}
              className={`
                px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0
                transition-colors duration-150 ease-in-out my-1
                ${activeFilter === tag
                  ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 ring-1 ring-purple-300 dark:ring-purple-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }
              `}
            >
              {tag}
            </button>
          ))}

          {/* Clear Filter Button */}
          {activeFilter && (
            <button
              onClick={clearFilter}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>

        {/* Controls Section */}
        <div className="flex items-center gap-2 pl-2 my-1">
          {/* Saved filter */}
          {isLoggedIn && (
            <button
              onClick={() => setShowSavedWebsites && setShowSavedWebsites(!showSavedWebsites)}
              className={`
                flex items-center justify-center gap-1 px-3 sm:px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium border
                ${showSavedWebsites
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-500 dark:border-purple-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800 dark:hover:bg-purple-900/20 dark:hover:text-purple-400 dark:hover:border-purple-800'}
              `}
            >
              <Star className={`h-4 w-4 ${showSavedWebsites ? 'fill-white' : ''}`} />
              <span className="hidden xs:inline">Saved</span>
            </button>
          )}

          {/* Sort dropdown */}
          <div 
            ref={sortMenuRef} 
            className="flex items-center relative"
          >
            <button
              onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              <currentSortOption.icon className="w-4 h-4" />
              {currentSortOption.label}
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Sort Dropdown Menu */}
      {isSortMenuOpen && (
        <div
          ref={sortDropdownRef}
          className="fixed z-50 mt-2 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700"
          style={{
            top: `${sortDropdownPosition.top}px`,
            left: `${sortDropdownPosition.left}px`,
          }}
        >
          <div className="py-1">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setSortMethod(option.value);
                  setIsSortMenuOpen(false);
                }}
                className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  sortMethod === option.value
                    ? 'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <option.icon className="w-4 h-4" />
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default WebsiteFilters;

